import { ForwardedRef, forwardRef } from "react";
import Select, { SelectInstance } from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { useLazyQuery } from "@apollo/client";
import useSelectStyles from "utils/useSelectStyles";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import { getFragmentData, gql } from "__generated__/gql";
import {
  RetailerChannelSelectOptionFragment,
  RetailerChannelSelectOptionsQuery,
} from "__generated__/gql/graphql";
import useFetchOptions from "components/Shared/Select/useFetchOptions";

type Option = RetailerChannelSelectOptionFragment;

const optionFragment = gql(/* GraphQL */ `
  fragment RetailerChannelSelectOption on RetailerChannel {
    id
    name
  }
`);

const valuesQuery = gql(/* GraphQL */ `
  query RetailerChannelSelectValue($ids: [UUID!]!) {
    retailerChannelsList(filter: { id: { in: $ids } }) {
      ...RetailerChannelSelectOption
    }
  }
`);

const optionsQuery = gql(/* GraphQL */ `
  query RetailerChannelSelectOptions {
    retailerChannelsList(orderBy: NAME_ASC) {
      ...RetailerChannelSelectOption
    }
  }
`);

function getOptions(data: RetailerChannelSelectOptionsQuery) {
  return getFragmentData(optionFragment, data?.retailerChannelsList);
}

type Props<IsMulti extends boolean = false> = Omit<
  StateManagerProps<Option, IsMulti>,
  "onChange" | "value"
> &
  IdValueSelectProps<IsMulti>;

function RetailerChannelSelect<IsMulti extends boolean = false>(
  { isMulti, onChange, value: rawValue, isLoading, ...rest }: Props<IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const styleProps = useSelectStyles<Option, IsMulti>();

  const idValueProps = useIdValue<Option, IsMulti, typeof valuesQuery>({
    getOptionsFromData: (data) =>
      getFragmentData(optionFragment, data.retailerChannelsList),
    isMulti,
    labelKey: "name",
    onChange,
    query: valuesQuery,
    value: rawValue,
  });

  const lazyQuery = useLazyQuery(optionsQuery);

  const fetchOptionsProps = useFetchOptions({
    getOptions,
    lazyQuery,
    menuIsOpen: rest.menuIsOpen,
  });

  return (
    <Select<Option, IsMulti>
      {...styleProps}
      {...idValueProps}
      {...fetchOptionsProps}
      {...{ isMulti, ref }}
      isLoading={
        idValueProps.isLoading || fetchOptionsProps.isLoading || isLoading
      }
      placeholder="Select a channel…"
      noOptionsMessage={() => "No channels found"}
      {...rest}
    />
  );
}

export default forwardRef(RetailerChannelSelect);
